import { SVGs } from "../assets";
import * as React from "react";
import { twMerge } from "tailwind-merge";
import Text from "./Text";
import TextField from "./TextField";
import { useOnClickOutside } from "../hooks";

export type InputOptionsOption = {
  label: string;
  subtitle?: string;
  value: any;
  icon?: React.ReactNode;
};
export type InputOptionsProps = {
  children: React.ReactNode;
  value?: string;
  onChange: (value: string, option: InputOptionsOption) => void;
  className?: string;
  classNames?: {
    menu?: string;
    search?: string;
    option?: string;
    trigger?: string;
  };
  options: InputOptionsOption[];
  searchable?: boolean;
  searchPlaceholder?: string;
  disabled?: boolean;
  position?: "left" | "right";
};

export default function InputOptions({
  children,
  value,
  onChange,
  className,
  options,
  classNames,
  searchable,
  searchPlaceholder,
  disabled,
  position = "left",
}: InputOptionsProps) {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const searchRef = React.useRef<HTMLInputElement | null>(null);
  const itemRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const { open, setOpen } = useOnClickOutside(rootRef);
  const lastInputRef = React.useRef<"mouse" | "keyboard">("mouse");
  const [query, setQuery] = React.useState("");
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);

  const filteredOptions = React.useMemo(() => {
    if (!searchable) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(({ label, subtitle, value: optionValue }) =>
      [label, subtitle, String(optionValue)]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q)),
    );
  }, [options, query, searchable]);

  const resolvedHighlightIndex = React.useMemo(() => {
    if (!filteredOptions.length) return 0;
    if (value != null && value !== "") {
      const selectedIdx = filteredOptions.findIndex(
        (opt) => opt.value === value,
      );
      if (selectedIdx >= 0) return selectedIdx;
    }
    return 0;
  }, [filteredOptions, value]);

  React.useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    if (searchable) searchRef.current?.focus();
  }, [open, searchable]);

  React.useEffect(() => {
    if (!open) return;
    setHighlightedIndex(resolvedHighlightIndex);
  }, [open, query, resolvedHighlightIndex]);

  const setActiveIndex = (idx: number) => {
    const next = Math.max(0, Math.min(idx, filteredOptions.length - 1));
    setHighlightedIndex(next);
    itemRefs.current[next]?.focus();
  };

  const moveHighlight = (delta: number) => {
    setActiveIndex(highlightedIndex + delta);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open || !filteredOptions.length) return;
    lastInputRef.current = "keyboard";

    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveHighlight(1);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      moveHighlight(-1);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      itemRefs.current[highlightedIndex]?.click();
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div
      ref={rootRef}
      onKeyDown={onKeyDown}
      className={twMerge("relative", className)}
    >
      <div
        children={children}
        className={classNames?.trigger}
        onMouseDown={() => {
          if (!disabled) setOpen(true);
        }}
        onFocusCapture={() => {
          if (!disabled) setOpen(true);
        }}
      />
      {open && (
        <div
          role="menu"
          onMouseMove={() => {
            lastInputRef.current = "mouse";
          }}
          className={twMerge(
            "absolute mt-[10px] z-20 w-full min-w-[150px] overflow-hidden rounded-[4px] bg-surface shadow-md",
            position === "right" ? "right-0" : "left-0",
            !searchable && "max-h-[300px] overflow-y-auto",
            classNames?.menu,
          )}
        >
          {searchable && (
            <div className="sticky top-0 z-10 border-b border-input-border bg-surface p-2">
              <TextField
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                classNames={{ container: classNames?.search }}
              />
            </div>
          )}
          <div
            className={twMerge(searchable && "max-h-[240px] overflow-y-auto")}
          >
            {filteredOptions.map((opt, idx) => (
              <button
                key={opt.value}
                type="button"
                role="menuitem"
                ref={(el) => {
                  itemRefs.current[idx] = el;
                }}
                className={twMerge(
                  "flex w-full items-center justify-between px-3 py-[10px] text-sm outline-none focus:bg-background",
                  highlightedIndex === idx && "bg-background",
                )}
                onMouseEnter={() => {
                  if (lastInputRef.current !== "mouse") return;
                  setActiveIndex(idx);
                }}
                onClick={() => {
                  onChange(opt.value, opt);
                  setOpen(false);
                }}
              >
                <div
                  className={twMerge(
                    "flex items-start gap-2",
                    classNames?.option,
                  )}
                >
                  {opt.icon}
                  <div className="min-w-0 text-start">
                    <Text>{opt.label}</Text>
                    {!!opt.subtitle && (
                      <Text level={5} className="text-text-secondary-light">
                        {opt.subtitle}
                      </Text>
                    )}
                  </div>
                </div>
                {opt.value === value && (
                  <img
                    src={SVGs.check}
                    alt="Check"
                    className="w-[18px] h-[18px]"
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
